import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Search,
  Download,
  Printer,
  Users,
  LayoutGrid,
  FileText,
} from 'lucide-react';

// ===== Types =====

interface SeatingChart {
  id: string;
  name: string;
  year: number;
  status: string;
  seatCount: number;
  createdAt: string;
}

interface SeatingSection {
  id: string;
  name: string;
  sortOrder: number;
  capacity: number | null;
  priceCategory: string | null;
  seatCount: number;
  assignedCount: number;
  seats: Seat[];
}

interface Seat {
  id: string;
  sectionId: string;
  row: string;
  number: number;
  seatType: string;
  status: string;
  assignment: SeatAssignment | null;
}

interface SeatAssignment {
  id: string;
  memberId: string | null;
  householdId: string | null;
  notes: string | null;
  assignedBy: string;
  createdAt: string;
}

interface ChartDetail extends SeatingChart {
  sections: SeatingSection[];
}

interface Assignment {
  id: string;
  seatId: string;
  sectionName: string;
  row: string;
  number: number;
  memberId: string | null;
  memberName: string | null;
  householdId: string | null;
  notes: string | null;
  assignedBy: string;
  createdAt: string;
}

interface AvailabilityData {
  totals: { total: number; assigned: number; available: number; held: number };
  sections: Array<{
    sectionId: string;
    sectionName: string;
    total: number;
    assigned: number;
    available: number;
    held: number;
  }>;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

// ===== Main Component =====

export function HighHolidaysPage() {
  const [activeTab, setActiveTab] = useState<'charts' | 'assignments' | 'reports'>('charts');

  const tabs = [
    { key: 'charts' as const, label: 'Seating Charts', icon: LayoutGrid },
    { key: 'assignments' as const, label: 'Assignments', icon: Users },
    { key: 'reports' as const, label: 'Reports', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">High Holidays</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage seating charts and seat assignments for High Holiday services
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium',
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'charts' && <ChartsTab />}
      {activeTab === 'assignments' && <AssignmentsTab />}
      {activeTab === 'reports' && <ReportsTab />}
    </div>
  );
}

// ===== Charts Tab =====

function ChartsTab() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [selectedChart, setSelectedChart] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: charts, isLoading } = useQuery({
    queryKey: ['seating-charts'],
    queryFn: async () => {
      const response = await api.get<SeatingChart[]>('/seating');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/seating/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seating-charts'] });
      setDeleteConfirm(null);
      if (selectedChart === deleteConfirm) setSelectedChart(null);
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'draft': return 'bg-yellow-100 text-yellow-700';
      case 'archived': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Seating Charts</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Create Chart
        </button>
      </div>

      {/* Charts List */}
      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seats</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(!charts || charts.length === 0) ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                  No seating charts yet. Create one to get started.
                </td>
              </tr>
            ) : (
              charts.map((chart) => (
                <tr
                  key={chart.id}
                  className={cn('hover:bg-gray-50 cursor-pointer', selectedChart === chart.id && 'bg-primary-50')}
                  onClick={() => setSelectedChart(selectedChart === chart.id ? null : chart.id)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{chart.name}</td>
                  <td className="px-4 py-3 text-gray-600">{chart.year}</td>
                  <td className="px-4 py-3">
                    <span className={cn('inline-flex rounded-full px-2 py-1 text-xs font-medium', getStatusColor(chart.status))}>
                      {chart.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{chart.seatCount}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(chart.id); }}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Chart Detail */}
      {selectedChart && <ChartDetail chartId={selectedChart} />}

      {/* Create Modal */}
      {showModal && (
        <CreateChartModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            queryClient.invalidateQueries({ queryKey: ['seating-charts'] });
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Chart</h3>
                <p className="text-sm text-gray-500">This will delete all sections, seats, and assignments.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Create Chart Modal =====

function CreateChartModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    year: new Date().getFullYear(),
    status: 'draft',
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/seating', formData);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => onCreated(),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Create Seating Chart</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Rosh Hashanah 5787"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !formData.name}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Chart'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Chart Detail =====

function ChartDetail({ chartId }: { chartId: string }) {
  const queryClient = useQueryClient();
  const [addSectionName, setAddSectionName] = useState('');
  const [generatingSeats, setGeneratingSeats] = useState<string | null>(null);
  const [seatRows, setSeatRows] = useState('');
  const [seatsPerRow, setSeatsPerRow] = useState('10');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const { data: chart, isLoading } = useQuery({
    queryKey: ['seating-chart', chartId],
    queryFn: async () => {
      const response = await api.get<ChartDetail>(`/seating/${chartId}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const addSectionMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/seating/${chartId}/sections`, {
        name: addSectionName,
        sortOrder: (chart?.sections?.length || 0),
      });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seating-chart', chartId] });
      queryClient.invalidateQueries({ queryKey: ['seating-charts'] });
      setAddSectionName('');
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      const response = await api.delete(`/seating/sections/${sectionId}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seating-chart', chartId] });
      queryClient.invalidateQueries({ queryKey: ['seating-charts'] });
    },
  });

  const generateSeatsMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      const rows = seatRows.split(',').map((r) => r.trim()).filter(Boolean);
      if (rows.length === 0) return;
      const response = await api.post(`/seating/sections/${sectionId}/seats`, {
        rows,
        seatsPerRow: parseInt(seatsPerRow),
      });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seating-chart', chartId] });
      queryClient.invalidateQueries({ queryKey: ['seating-charts'] });
      setGeneratingSeats(null);
      setSeatRows('');
      setSeatsPerRow('10');
    },
  });

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!chart) return null;

  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{chart.name} - Sections</h3>
      </div>

      {/* Add Section */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={addSectionName}
          onChange={(e) => setAddSectionName(e.target.value)}
          placeholder="New section name..."
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <button
          onClick={() => addSectionMutation.mutate()}
          disabled={!addSectionName || addSectionMutation.isPending}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add Section
        </button>
      </div>

      {/* Sections List */}
      <div className="space-y-3">
        {chart.sections?.map((section) => (
          <div key={section.id} className="border border-gray-200 rounded-lg">
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleSection(section.id)}
            >
              <div className="flex items-center gap-3">
                {expandedSections.has(section.id)
                  ? <ChevronDown className="h-4 w-4 text-gray-400" />
                  : <ChevronRight className="h-4 w-4 text-gray-400" />
                }
                <span className="font-medium text-gray-900">{section.name}</span>
                <span className="text-sm text-gray-500">
                  {section.seatCount} seats ({section.assignedCount} assigned)
                </span>
                {section.priceCategory && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{section.priceCategory}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setGeneratingSeats(generatingSeats === section.id ? null : section.id);
                  }}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  Generate Seats
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSectionMutation.mutate(section.id); }}
                  className="p-1 text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Generate Seats Form */}
            {generatingSeats === section.id && (
              <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rows (comma-separated)</label>
                    <input
                      type="text"
                      value={seatRows}
                      onChange={(e) => setSeatRows(e.target.value)}
                      placeholder="A, B, C, D"
                      className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Seats per Row</label>
                    <input
                      type="number"
                      value={seatsPerRow}
                      onChange={(e) => setSeatsPerRow(e.target.value)}
                      min="1"
                      className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <button
                    onClick={() => generateSeatsMutation.mutate(section.id)}
                    disabled={generateSeatsMutation.isPending || !seatRows}
                    className="rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {generateSeatsMutation.isPending ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </div>
            )}

            {/* Seat Grid */}
            {expandedSections.has(section.id) && section.seats && section.seats.length > 0 && (
              <div className="border-t border-gray-200 px-4 py-3">
                <SeatGrid seats={section.seats} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Seat Grid (read-only in Charts tab) =====

function SeatGrid({ seats }: { seats: Seat[] }) {
  const rows = [...new Set(seats.map((s) => s.row))].sort();

  const getSeatColor = (seat: Seat) => {
    if (seat.seatType === 'accessible') return 'bg-gray-200 text-gray-600';
    switch (seat.status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'assigned': return 'bg-blue-100 text-blue-700';
      case 'held': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-1">
      {rows.map((row) => {
        const rowSeats = seats.filter((s) => s.row === row).sort((a, b) => a.number - b.number);
        return (
          <div key={row} className="flex items-center gap-1">
            <span className="w-6 text-xs font-medium text-gray-500 text-center">{row}</span>
            {rowSeats.map((seat) => (
              <div
                key={seat.id}
                className={cn('w-7 h-7 rounded text-xs flex items-center justify-center font-medium', getSeatColor(seat))}
                title={`${row}-${seat.number} (${seat.status})`}
              >
                {seat.number}
              </div>
            ))}
          </div>
        );
      })}
      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100" /> Assigned</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-100" /> Held</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-200" /> Accessible</span>
      </div>
    </div>
  );
}

// ===== Assignments Tab =====

function AssignmentsTab() {
  const queryClient = useQueryClient();
  const [selectedChartId, setSelectedChartId] = useState('');
  const [assigningSeat, setAssigningSeat] = useState<Seat | null>(null);
  const [viewingAssignment, setViewingAssignment] = useState<Seat | null>(null);

  const { data: charts } = useQuery({
    queryKey: ['seating-charts'],
    queryFn: async () => {
      const response = await api.get<SeatingChart[]>('/seating');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: chart } = useQuery({
    queryKey: ['seating-chart', selectedChartId],
    queryFn: async () => {
      const response = await api.get<ChartDetail>(`/seating/${selectedChartId}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: !!selectedChartId,
  });

  const { data: availability } = useQuery({
    queryKey: ['seating-availability', selectedChartId],
    queryFn: async () => {
      const response = await api.get<AvailabilityData>(`/seating/${selectedChartId}/availability`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: !!selectedChartId,
  });

  const releaseMutation = useMutation({
    mutationFn: async (seatId: string) => {
      const response = await api.delete(`/seating/seats/${seatId}/assign`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seating-chart', selectedChartId] });
      queryClient.invalidateQueries({ queryKey: ['seating-availability', selectedChartId] });
      setViewingAssignment(null);
    },
  });

  const getSeatColor = (seat: Seat) => {
    if (seat.seatType === 'accessible') return 'bg-gray-200 text-gray-600 hover:bg-gray-300';
    if (seat.seatType === 'reserved') return 'bg-gray-300 text-gray-600';
    switch (seat.status) {
      case 'available': return 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer';
      case 'assigned': return 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer';
      case 'held': return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getMemberInitials = (seat: Seat) => {
    if (!seat.assignment?.memberId) return '';
    // We don't have the member name in the seat data, show a dot indicator
    return '**';
  };

  const handleSeatClick = (seat: Seat) => {
    if (seat.seatType === 'reserved') return;
    if (seat.status === 'available' || seat.status === 'held') {
      setAssigningSeat(seat);
    } else if (seat.status === 'assigned' && seat.assignment) {
      setViewingAssignment(seat);
    }
  };

  return (
    <div className="space-y-6">
      {/* Chart Selector */}
      <div className="flex items-center gap-4">
        <select
          value={selectedChartId}
          onChange={(e) => setSelectedChartId(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select a chart...</option>
          {charts?.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.year})</option>
          ))}
        </select>
      </div>

      {/* Stats Bar */}
      {availability && (
        <div className="grid grid-cols-4 gap-4">
          <div className="rounded-lg bg-white border border-gray-200 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{availability.totals.total}</p>
            <p className="text-sm text-gray-500">Total Seats</p>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{availability.totals.assigned}</p>
            <p className="text-sm text-blue-600">Assigned</p>
          </div>
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{availability.totals.available}</p>
            <p className="text-sm text-green-600">Available</p>
          </div>
          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4 text-center">
            <p className="text-2xl font-bold text-yellow-700">{availability.totals.held}</p>
            <p className="text-sm text-yellow-600">Held</p>
          </div>
        </div>
      )}

      {/* Seat Grid by Section */}
      {chart?.sections?.map((section) => {
        const rows = [...new Set(section.seats.map((s) => s.row))].sort();
        return (
          <div key={section.id} className="rounded-xl bg-white shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">{section.name}</h3>
            <div className="space-y-1">
              {rows.map((row) => {
                const rowSeats = section.seats.filter((s) => s.row === row).sort((a, b) => a.number - b.number);
                return (
                  <div key={row} className="flex items-center gap-1">
                    <span className="w-6 text-xs font-medium text-gray-500 text-center">{row}</span>
                    {rowSeats.map((seat) => (
                      <div
                        key={seat.id}
                        onClick={() => handleSeatClick(seat)}
                        className={cn('w-8 h-8 rounded text-xs flex items-center justify-center font-medium transition-colors', getSeatColor(seat))}
                        title={`Row ${row}, Seat ${seat.number} (${seat.status})`}
                      >
                        {seat.status === 'assigned' ? getMemberInitials(seat) : seat.number}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {!selectedChartId && (
        <div className="text-center py-12 text-gray-500">
          Select a seating chart to view and manage assignments
        </div>
      )}

      {/* Assign Seat Modal */}
      {assigningSeat && (
        <AssignSeatModal
          seat={assigningSeat}
          chartId={selectedChartId}
          onClose={() => setAssigningSeat(null)}
          onAssigned={() => {
            setAssigningSeat(null);
            queryClient.invalidateQueries({ queryKey: ['seating-chart', selectedChartId] });
            queryClient.invalidateQueries({ queryKey: ['seating-availability', selectedChartId] });
          }}
        />
      )}

      {/* View Assignment Modal */}
      {viewingAssignment && viewingAssignment.assignment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Seat Assignment</h3>
              <button onClick={() => setViewingAssignment(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">Row:</span> <span className="font-medium">{viewingAssignment.row}</span></p>
              <p><span className="text-gray-500">Seat:</span> <span className="font-medium">{viewingAssignment.number}</span></p>
              {viewingAssignment.assignment.notes && (
                <p><span className="text-gray-500">Notes:</span> {viewingAssignment.assignment.notes}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => releaseMutation.mutate(viewingAssignment.id)}
                disabled={releaseMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
              >
                {releaseMutation.isPending ? 'Releasing...' : 'Release Seat'}
              </button>
              <button onClick={() => setViewingAssignment(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Assign Seat Modal =====

function AssignSeatModal({
  seat,
  chartId,
  onClose,
  onAssigned,
}: {
  seat: Seat;
  chartId: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [notes, setNotes] = useState('');

  const { data: members } = useQuery({
    queryKey: ['member-search', searchQuery],
    queryFn: async () => {
      const response = await api.get<Member[]>(`/members?search=${encodeURIComponent(searchQuery)}&limit=10`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: searchQuery.length >= 2,
  });

  const assignMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/seating/seats/${seat.id}/assign`, {
        memberId: selectedMember?.id,
        notes: notes || undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => onAssigned(),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Assign Seat (Row {seat.row}, #{seat.number})
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Member Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search Member</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSelectedMember(null); }}
                placeholder="Type name to search..."
                className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            {members && members.length > 0 && !selectedMember && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {members.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setSelectedMember(m);
                      setSearchQuery(`${m.firstName} ${m.lastName}`);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    {m.firstName} {m.lastName}
                    {m.email && <span className="text-gray-400 ml-2">{m.email}</span>}
                  </button>
                ))}
              </div>
            )}
            {selectedMember && (
              <p className="mt-2 text-sm text-green-600">Selected: {selectedMember.firstName} {selectedMember.lastName}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={() => assignMutation.mutate()}
            disabled={assignMutation.isPending || !selectedMember}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
          >
            {assignMutation.isPending ? 'Assigning...' : 'Assign Seat'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Reports Tab =====

function ReportsTab() {
  const [selectedChartId, setSelectedChartId] = useState('');

  const { data: charts } = useQuery({
    queryKey: ['seating-charts'],
    queryFn: async () => {
      const response = await api.get<SeatingChart[]>('/seating');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: assignments } = useQuery({
    queryKey: ['seating-assignments', selectedChartId],
    queryFn: async () => {
      const response = await api.get<Assignment[]>(`/seating/${selectedChartId}/assignments`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: !!selectedChartId,
  });

  const { data: availability } = useQuery({
    queryKey: ['seating-availability', selectedChartId],
    queryFn: async () => {
      const response = await api.get<AvailabilityData>(`/seating/${selectedChartId}/availability`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    enabled: !!selectedChartId,
  });

  const exportCSV = useCallback(() => {
    if (!assignments) return;
    const headers = ['Member Name', 'Section', 'Row', 'Seat', 'Notes'];
    const rows = assignments.map((a) => [
      a.memberName || 'N/A',
      a.sectionName,
      a.row,
      a.number.toString(),
      a.notes || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'seating-assignments.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [assignments]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Chart Selector & Actions */}
      <div className="flex items-center justify-between">
        <select
          value={selectedChartId}
          onChange={(e) => setSelectedChartId(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select a chart...</option>
          {charts?.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.year})</option>
          ))}
        </select>
        {selectedChartId && (
          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
          </div>
        )}
      </div>

      {/* Section Summary */}
      {availability && (
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Section Summary</h3>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Section</th>
                <th className="pb-2">Total</th>
                <th className="pb-2">Assigned</th>
                <th className="pb-2">Available</th>
                <th className="pb-2">Held</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {availability.sections.map((s) => (
                <tr key={s.sectionId}>
                  <td className="py-2 font-medium text-gray-900">{s.sectionName}</td>
                  <td className="py-2 text-gray-600">{s.total}</td>
                  <td className="py-2 text-blue-600">{s.assigned}</td>
                  <td className="py-2 text-green-600">{s.available}</td>
                  <td className="py-2 text-yellow-600">{s.held}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-2 text-gray-900">Total</td>
                <td className="py-2">{availability.totals.total}</td>
                <td className="py-2 text-blue-600">{availability.totals.assigned}</td>
                <td className="py-2 text-green-600">{availability.totals.available}</td>
                <td className="py-2 text-yellow-600">{availability.totals.held}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Assignments Table */}
      {assignments && assignments.length > 0 && (
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Section</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seat</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.memberName || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{a.sectionName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{a.row}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{a.number}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{a.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedChartId && assignments && assignments.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No assignments for this chart yet.
        </div>
      )}

      {!selectedChartId && (
        <div className="text-center py-12 text-gray-500">
          Select a seating chart to view reports
        </div>
      )}
    </div>
  );
}
